import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config';
import './NotifToast.css';

const NotifToast = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null); // { id, message }
  const [hiding, setHiding] = useState(false);
  const prevIdsRef = useRef(new Set());
  const timerRef = useRef(null);

  const fetchAndCheck = useCallback(async () => {
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const notifs = data.notifications || [];

      // Find new notifications not seen before
      const currentIds = new Set(notifs.map(n => n.id));
      for (const n of notifs) {
        if (!prevIdsRef.current.has(n.id)) {
          // New notification — show toast
          showToast(n);
          break; // Show one at a time
        }
      }
      prevIdsRef.current = currentIds;
    } catch { /* ignore */ }
  }, []);

  const showToast = (notif) => {
    clearTimeout(timerRef.current);
    setHiding(false);
    setToast({
      id: notif.id,
      message: notif.message || 'Yeni bildirim',
      actor: notif.actor,
      entityType: notif.entityType,
      entityId: notif.entityId,
    });
    timerRef.current = setTimeout(() => {
      setHiding(true);
      setTimeout(() => { setToast(null); setHiding(false); }, 400);
    }, 5000);
  };

  const handleToastClick = () => {
    if (!toast) return;
    if (toast.entityType === 'project_invitation' && toast.entityId) {
      navigate(`/projects?invitation=${toast.entityId}`, { state: { ts: Date.now() } });
    } else if (toast.entityType === 'project' && toast.entityId) {
      navigate(`/projects?id=${toast.entityId}`, { state: { ts: Date.now() } });
    } else if (toast.entityType === 'organization' && toast.entityId) {
      navigate(`/groups?org=${toast.entityId}`, { state: { ts: Date.now() } });
    } else if (toast.entityType === 'team' && toast.entityId) {
      navigate(`/groups?team=${toast.entityId}`, { state: { ts: Date.now() } });
    } else if (toast.entityType === 'message' && toast.entityId) {
      navigate(`/messages?conv=${toast.entityId}`, { state: { ts: Date.now() } });
    }
    dismiss();
  };

  const dismiss = () => {
    clearTimeout(timerRef.current);
    setHiding(true);
    setTimeout(() => { setToast(null); setHiding(false); }, 400);
  };

  // Poll every 15 seconds
  useEffect(() => {
    // Initial load — seed known IDs without showing toast
    const init = async () => {
      const token = localStorage.getItem('projabze_token');
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/notifications?unread_only=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          prevIdsRef.current = new Set((data.notifications || []).map(n => n.id));
        }
      } catch { /* ignore */ }
    };
    init();
    const interval = setInterval(fetchAndCheck, 15000);
    return () => clearInterval(interval);
  }, [fetchAndCheck]);

  if (!toast) return null;

  const actorName = toast.actor?.firstName
    ? `${toast.actor.firstName} ${toast.actor.lastName || ''}`.trim()
    : null;

  return (
    <div className={`notif-toast ${hiding ? 'notif-toast-hide' : ''}`} onClick={handleToastClick} style={{ cursor: 'pointer' }}>
      <div className="notif-toast-icon">&#128276;</div>
      <div className="notif-toast-body">
        {actorName && <span className="notif-toast-actor">{actorName}</span>}
        <span className="notif-toast-msg">{toast.message}</span>
      </div>
      <button className="notif-toast-close" onClick={(e) => { e.stopPropagation(); dismiss(); }}>&times;</button>
    </div>
  );
};

export default NotifToast;
